import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Skeleton,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getPEOReportList } from '../../endpoints/peoReportsTs';
import { PEOReport } from '../../types/peoReports';

const PEOReportsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    return localStorage.getItem('peo-reports-banner-dismissed') === 'true';
  });

  const { data, isLoading, error } = useApiGet<{ reports: PEOReport[] }>(
    ['peo-reports-list'],
    getPEOReportList,
    {
      enabled: true,
      retry: 1,
    }
  );

  const reports = data?.reports || [];

  const filteredReports = reports.filter((report) => {
    const query = searchQuery.toLowerCase();
    return (
      report.name.toLowerCase().includes(query) ||
      report.description.toLowerCase().includes(query) ||
      report.group_type_name.toLowerCase().includes(query)
    );
  });

  const handleDismissBanner = () => {
    localStorage.setItem('peo-reports-banner-dismissed', 'true');
    setBannerDismissed(true);
  };

  const getAccessScopeColor = (
    scope: string
  ):
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error' => {
    switch (scope) {
      case 'international':
        return 'secondary';
      case 'spd':
        return 'info';
      case 'chapter':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Banner for old reports link */}
      {!bannerDismissed && (
        <Alert
          severity="info"
          icon={<AssessmentIcon />}
          action={
            <IconButton
              size="small"
              onClick={handleDismissBanner}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ mb: 3 }}
        >
          Looking for the previous reports system?{' '}
          <RouterLink
            to="/reports"
            style={{
              color: 'inherit',
              fontWeight: 600,
              textDecoration: 'underline',
            }}
          >
            Use previous Hub Reports
          </RouterLink>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          PEO Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a report to view and download data
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search reports by name, description, or group..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 600 }}
      />

      {/* Loading State */}
      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={n}>
              <Skeleton
                variant="rectangular"
                height={180}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error">
          Failed to load reports. Please try again later.
        </Alert>
      )}

      {/* Reports Grid */}
      {!isLoading && !error && (
        <>
          {filteredReports.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <AssessmentIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {reports.length === 0
                  ? 'No Reports Available'
                  : 'No Reports Found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {reports.length === 0
                  ? 'You do not have access to any reports at this time.'
                  : 'Try adjusting your search query.'}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredReports.map((report) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => navigate(`/peo-reports/${report.id}`)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Access Scope Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                        }}
                      >
                        <Chip
                          label={report.access_scope}
                          size="small"
                          color={getAccessScopeColor(report.access_scope)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>

                      {/* Report Name */}
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          pr: 8, // Make room for badge
                        }}
                      >
                        {report.name}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minHeight: '3.6em',
                        }}
                      >
                        {report.description}
                      </Typography>

                      {/* Group Info */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {report.group_type_name} • {report.group_name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default PEOReportsList;
